import * as React from 'react';
import Box from '@mui/joy/Box';
import CircularProgress from '@mui/joy/CircularProgress';
import Accordion from '@mui/joy/Accordion';
import AccordionSummary from '@mui/joy/AccordionSummary';
import AccordionDetails from '@mui/joy/AccordionDetails';
import Grid from '@mui/joy/Grid';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import Stack from '@mui/joy/Stack';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import { FilterBar } from './components';
import { DailyProfitTable } from './components';
import { SummaryHeader } from './components';
import { ProfitCard } from './components';
import { UsersCard } from './components';
import { BalanceCard } from './components';
import { OrdersCard } from './components';
import { WithdrawalsCard } from './components';
import { DepositsCard } from './components';
import { useDashboardData } from './hooks/useDashboardData';
import { toInputValue, subtractMonths } from './utils/date';
const CombinedProfitChartLazy = React.lazy(() => import('./components/CombinedProfitChart').then(m => ({ default: m.CombinedProfitChart })));
const UsersChartLazy = React.lazy(() => import('./components/UsersChart').then(m => ({ default: m.UsersChart })));

export default function Dashboard() {
    const [fromDate, setFromDate] = React.useState<string>(() => {
        const now = new Date();
        const from = subtractMonths(now, 1);
        return toInputValue(from);
    });
    const [toDate, setToDate] = React.useState<string>(() => toInputValue(new Date()));
    const [accordionExpanded, setAccordionExpanded] = React.useState<boolean>(() => {
        try {
            const saved = localStorage.getItem('dashboardAccordionExpanded');
            return saved ? saved === 'true' : false;
        } catch {
            return false;
        }
    });
    const { loading, data } = useDashboardData({
        fromIso: new Date(fromDate).toISOString(),
        toIso: new Date(toDate).toISOString(),
    });

    return (
        <Box
            component="main"
            className="MainContent"
            sx={{
                px: { xs: 2, md: 6 },
                pt: {
                    xs: 'calc(12px + var(--Header-height))',
                    sm: 'calc(12px + var(--Header-height))',
                    md: 3,
                },
                pb: { xs: 2, sm: 2, md: 3 },
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minWidth: 0,
                gap: 1,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    mb: 1,
                    gap: 1,
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'start', sm: 'center' },
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                }}
            >
                <Typography level="h2" component="h1">
                    Главная
                </Typography>
            </Box>
            <FilterBar
                loading={loading}
                onApplyRange={(from, to) => { setFromDate(from); setToDate(to); }}
            />

            <Accordion
                expanded={accordionExpanded}
                onChange={(_, expanded) => {
                    const next = !!expanded;
                    setAccordionExpanded(next);
                    try {
                        localStorage.setItem('dashboardAccordionExpanded', String(next));
                    } catch {
                        // ignore
                    }
                }}
            >
                <AccordionSummary>
                    <SummaryHeader loading={loading} data={data ?? null} />
                </AccordionSummary>
                <AccordionDetails>
            {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6 }}>
                    <CircularProgress size="md" />
                </Box>
            ) : (
            <Grid container spacing={2} sx={{ alignItems: 'flex-start' }}>

                <Grid xs={12} md={3}>
                    <Stack direction={'column'} spacing={1}>
                        <ProfitCard data={data ?? null} />
                        <UsersCard data={data ?? null} />
                        <BalanceCard data={data ?? null} />
                    </Stack>
                </Grid>

                <Grid xs={12} md={3}>
                    <OrdersCard data={data ?? null} />
                </Grid>
                <Grid xs={12} md={3}>
                    <WithdrawalsCard data={data ?? null} />
                </Grid>
                <Grid xs={12} md={3}>
                    <DepositsCard data={data ?? null} />
                </Grid>
            </Grid>
            )}
                </AccordionDetails>
            </Accordion>
            {/* Daily Profit Table - bottom of page */}
            <Sheet variant="outlined" sx={{ p: 2, borderRadius: '8px', mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <TrendingUpRoundedIcon fontSize="small" />
					<Typography level="title-md">Профит по дням (за период)</Typography>
                </Box>
                <DailyProfitTable data={data ?? null} fromDate={fromDate} toDate={toDate} />
            </Sheet>

			{/* Combined profit chart - placed below the table */}
			<Sheet variant="outlined" sx={{ p: 2, borderRadius: '8px', mt: 2 }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
						<TrendingUpRoundedIcon fontSize="small" />
						<Typography level="title-md">Профит: депозиты, выводы, ордера</Typography>
					</Box>
				<React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size="sm" /></Box>}>
                    <CombinedProfitChartLazy data={data ?? null} fromDate={fromDate} toDate={toDate} />
				</React.Suspense>
			</Sheet>

			{/* Users chart - bottom of page */}
			<Sheet variant="outlined" sx={{ p: 2, borderRadius: '8px', mt: 2 }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
						<TrendingUpRoundedIcon fontSize="small" />
						<Typography level="title-md">Кол-во зарегистрированных пользователей</Typography>
					</Box>
				<React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size="sm" /></Box>}>
					<UsersChartLazy data={data ?? null} fromDate={fromDate} toDate={toDate} />
				</React.Suspense>
			</Sheet>
        </Box>
    );
}


